import { useEffect, useState } from 'react';
import { Badge, Fab, Tooltip } from '@mui/material';
import FeedbackOutlinedIcon from '@mui/icons-material/FeedbackOutlined';
import { useTranslation } from 'react-i18next';
import { useGetAuthApi } from '@/hooks/UseApiCalls';
import apiClient from '@/api/ApiClient';
import User from '@/models/User';
import styles from './SprintsReportWidget.module.scss';

interface SprintsWidgetConfig {
  enabled: boolean;
  widgetKey?: string;
  baseUrl?: string;
}

declare global {
  interface Window {
    SprintsReport?: {
      init: (options: object) => void;
      open: () => void;
      close: () => void;
      toggle: () => void;
      destroy?: () => void;
    };
  }
}

// The widget SDK is a page-level singleton — init once per page load,
// even if the component remounts (e.g. logout/login without a refresh).
// The unread count survives remounts the same way: the SDK's onUnread was
// registered at init time, so it lives here and the mounted component
// subscribes to it.
let widgetInitialized = false;
let widgetReady = false;
let unreadCount = 0;
let notifyComponent: ((state: { ready: boolean; unread: number }) => void) | null = null;

const publishToComponent = () => notifyComponent?.({ ready: widgetReady, unread: unreadCount });

const loadWidgetScript = (baseUrl: string): Promise<void> =>
  new Promise((resolve, reject) => {
    if (window.SprintsReport) {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.src = `${baseUrl.replace(/\/$/, '')}/widget/v1/sprints-widget.js`;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load Sprints widget script'));
    document.head.appendChild(script);
  });

// Read the JWT from storage at call time (not from a closure) so the widget
// keeps working after the app refreshes the logged-in user
const fetchWidgetToken = async (): Promise<string> => {
  const storedUser = localStorage.getItem('currentUser');
  const currentUser: User | null = storedUser ? JSON.parse(storedUser) : null;
  const { data } = await apiClient.post<{ token: string }>('sprints-widget/token', undefined, {
    headers: { Authorization: `Bearer ${currentUser?.token}` },
  });
  return data.token;
};

/**
 * The Sprints bug-report widget ("דיווח טכני"), opened from our own floating
 * button instead of the SDK's built-in launcher (`launcher: false`): the SDK's
 * launcher sits at the bottom corner where it covers the accessibility button,
 * while ours keeps the exact spot and look of the old feedback FAB (above the
 * accessibility button). The panel itself still lives in the SDK's iframe.
 * Enabled per site via the server's SprintsWidget settings.
 */
const SprintsReportWidget: React.FC = () => {
  const { t } = useTranslation();
  const { data: config } = useGetAuthApi<SprintsWidgetConfig>('sprints-widget/config');
  const [{ ready, unread }, setWidgetState] = useState({ ready: widgetReady, unread: unreadCount });

  useEffect(() => {
    notifyComponent = setWidgetState;
    return () => {
      notifyComponent = null;
    };
  }, []);

  useEffect(() => {
    if (!config?.enabled || !config.widgetKey || !config.baseUrl || widgetInitialized) return;
    widgetInitialized = true;

    loadWidgetScript(config.baseUrl)
      .then(() => {
        window.SprintsReport?.init({
          widgetKey: config.widgetKey,
          token: fetchWidgetToken,
          theme: 'light',
          launcher: false,
          onUnread: (count: number) => {
            unreadCount = count;
            publishToComponent();
          },
          context: { site: import.meta.env.VITE_APP_SITE_ID },
        });
        widgetReady = true;
        publishToComponent();
      })
      .catch((error) => {
        widgetInitialized = false;
        console.error('Sprints widget failed to load:', error);
      });
  }, [config]);

  if (!ready) return null;

  return (
    <Tooltip title={t('sprintsWidget.tooltip')} placement="right">
      {/* data-sprints-widget-hide: the SDK hides it in screenshots and uses it
          for its close-from-outside handling (a host launcher without the
          attribute gets a dead close button — SDK issue #1038) */}
      <span className={styles.fabWrapper} data-sprints-widget-hide>
        <Badge
          badgeContent={unread}
          color="error"
          overlap="circular"
          classes={{ badge: styles.badge }}
        >
          <Fab
            size="medium"
            aria-label={t('sprintsWidget.tooltip')}
            className={styles.fab}
            onClick={() => window.SprintsReport?.toggle()}
          >
            <FeedbackOutlinedIcon />
          </Fab>
        </Badge>
      </span>
    </Tooltip>
  );
};

export default SprintsReportWidget;
